import { t } from '@lingui/core/macro';
import type { OrganisationIdentityFormValues } from '@duke-hq/svc-api/admin/trpc/routers/organisation/schemas';
import type { ColourFieldName } from './ColourField';

export type FieldDefinition =
  | {
      kind: 'text';
      name: keyof OrganisationIdentityFormValues;
      label: string;
      helper?: string;
      isRequired?: boolean;
    }
  | { kind: 'colour'; name: ColourFieldName; label: string; helper?: string };

export type IdentityGroup = {
  title: string;
  description: string;
  fields: FieldDefinition[];
};

/**
 * One page, grouped rather than split: eight of the thirteen fields are a line of
 * text each, and a page per group left each one mostly empty.
 *
 * A function rather than a constant so the labels are translated when the page
 * renders, not once when this module is first imported.
 */
export const IDENTITY_GROUPS = (): IdentityGroup[] => [
  {
    title: t`Details`,
    description: t`How this organisation names itself to students and staff.`,
    fields: [
      { kind: 'text', name: 'organisationName', label: t`Organisation name`, isRequired: true },
      {
        kind: 'text',
        name: 'legalName',
        label: t`Legal name`,
        helper: t`Your registered entity, used in copyright lines and other formal notices. If blank, the organisation name is used.`,
      },
      {
        kind: 'text',
        name: 'rtoCode',
        label: t`RTO code`,
        helper: t`Shown beside the organisation name`,
      },
    ],
  },
  {
    title: t`Support contact`,
    description: t`How students reach you. Shown anywhere your contact details appear.`,
    fields: [
      { kind: 'text', name: 'supportEmail', label: t`Support email` },
      { kind: 'text', name: 'supportPhoneDigits', label: t`Number dialled` },
      {
        kind: 'text',
        name: 'supportPhoneVanity',
        label: t`Number shown`,
        helper: t`For a vanity number spelled with letters. The dialled number follows it in brackets.`,
      },
    ],
  },
  {
    // The organisation's own pages, carried wherever it identifies itself. A link
    // that appears in one sentence of one email is part of that email's wording.
    title: t`Links`,
    description: t`Leave either blank if you do not have one.`,
    fields: [
      { kind: 'text', name: 'contactUrl', label: t`Contact page` },
      { kind: 'text', name: 'privacyNoticeUrl', label: t`Privacy notice` },
    ],
  },
  {
    title: t`Logos`,
    description: t`Used on emails, certificates and anywhere else your branding appears. Both must be reachable from the public internet.`,
    fields: [
      { kind: 'text', name: 'logoUrl', label: t`Logo`, helper: t`Your main, full-colour logo` },
      {
        kind: 'text',
        name: 'logoGreyscaleUrl',
        label: t`Greyscale logo`,
        helper: t`A single-colour version, for places a full-colour logo does not suit`,
      },
    ],
  },
  {
    // Separate from the logos above because these are the one part of the page
    // that only email uses: they style the links and buttons a template renders.
    title: t`Email colours`,
    description: t`How links and buttons look in the emails you send. The preview follows what you type.`,
    fields: [
      { kind: 'colour', name: 'linkColour', label: t`Link colour` },
      { kind: 'colour', name: 'buttonBackgroundColour', label: t`Button colour` },
      { kind: 'colour', name: 'buttonTextColour', label: t`Button text colour` },
    ],
  },
];
